/* Where the calculator's two prices come from.
 *
 * A price is either the market's — the yarn sheet's figure for the count,
 * the latest CAI rate for the cotton's grade — or one somebody typed. The
 * page keeps both: the typed figure stays in state under an `auto` flag, so
 * switching back to the market and then away again loses nothing.
 *
 * Nothing here talks to the server. The compute call is handed plain
 * numbers, resolved by these functions first, so the model on the server
 * has no idea a market exists and old callers keep working.
 */

import type {
  CottonLot, Market, MarketCount, MarketGrade, MarketYarn, Product, ProfitInputs, SinglePricing,
} from '../../lib/api'

export const countKey = (count?: number | null, quality?: string | null) =>
  count != null && quality ? `${count}|${quality}` : ''

export function findCount(
  market: Market | null, count?: number | null, quality?: string | null,
): MarketCount | null {
  if (!market || count == null || !quality) return null
  return market.counts.find((c) => c.count === count && c.quality === quality) ?? null
}

export const findGrade = (market: Market | null, key: string | null): MarketGrade | null =>
  (key && market?.cotton.grades.find((g) => g.key === key)) || null

/** The market's yarn price for a row, if the row names a count it prices. */
export const yarnQuote = (
  market: Market | null, p: { count?: number | null; quality?: string | null },
): MarketYarn | null => findCount(market, p.count, p.quality)?.yarn ?? null

/** What a cotton costs right now, and the CAI rate behind it when it is
 *  following one. */
export function lotPrice(lot: CottonLot, market: Market | null): {
  candy: number; live: MarketGrade | null
} {
  const grade = findGrade(market, lot.grade)
  return lot.auto && grade ? { candy: grade.candy, live: grade } : { candy: lot.candy, live: null }
}

/** "A", "B", … — a cotton is named by its place in the list, so two lots of
 *  one grade (one following the market, one typed) stay distinguishable. */
export const lotLetter = (i: number) => String.fromCharCode(65 + (i % 26))

export function lotLabel(lot: CottonLot, i: number, market: Market | null): string {
  const g = findGrade(market, lot.grade)
  const what = !g ? 'own price' : g.mm != null ? `${g.market} ${g.mm} mm` : g.market
  return `${lotLetter(i)} · ${what}`
}

const nextLotId = (lots: CottonLot[]) =>
  'c' + (Math.max(0, ...lots.map((l) => Number(l.id.slice(1)) || 0)) + 1)

/** The cotton a count is spun from by default: the lot already following
 *  the count's grade, or a new one. Returns the list it belongs in. */
export function lotForGrade(
  lots: CottonLot[], market: Market | null, grade: string | null, fallbackCandy: number,
): { lots: CottonLot[]; id: string } {
  const g = findGrade(market, grade)
  if (g) {
    const have = lots.find((l) => l.grade === g.key && l.auto)
    if (have) return { lots, id: have.id }
    const lot: CottonLot = { id: nextLotId(lots), grade: g.key, auto: true, candy: g.candy }
    return { lots: [...lots, lot], id: lot.id }
  }
  const have = lots.find((l) => !l.auto && l.candy === fallbackCandy)
  if (have) return { lots, id: have.id }
  const lot: CottonLot = { id: nextLotId(lots), grade: null, auto: false, candy: fallbackCandy }
  return { lots: [...lots, lot], id: lot.id }
}

/** The plant's default mix, priced: every row on its count's yarn price and
 *  its count's cotton, with one lot per grade the mix needs. */
export function seedMix(mix: Product[], market: Market | null): {
  products: Product[]; lots: CottonLot[]
} {
  let lots: CottonLot[] = []
  const products = mix.map((p) => {
    const mc = findCount(market, p.count, p.quality)
    const got = lotForGrade(lots, market, mc?.grade ?? null, p.candy)
    lots = got.lots
    return { ...p, cotton: got.id, yarnAuto: Boolean(mc?.yarn) }
  })
  return { products, lots }
}

/** A saved case from before cottons were kept in one place carried a price
 *  on every row. Those become typed lots, one per distinct price, so the
 *  case opens on exactly the numbers it was saved with. */
export function adoptSaved(products: Product[], saved?: CottonLot[]): {
  products: Product[]; lots: CottonLot[]
} {
  let lots: CottonLot[] = saved ? [...saved] : []
  const out = products.map((p) => {
    if (p.cotton && lots.some((l) => l.id === p.cotton)) return p
    const got = lotForGrade(lots, null, null, p.candy)
    lots = got.lots
    return { ...p, cotton: got.id, yarnAuto: p.yarnAuto ?? false }
  })
  return { products: out, lots }
}

/** The mix as the model needs it: every row carrying its two prices. */
export function resolveProducts(
  products: Product[], lots: CottonLot[], market: Market | null,
): Product[] {
  return products.map((p) => {
    const quote = p.yarnAuto ? yarnQuote(market, p) : null
    const lot = lots.find((l) => l.id === p.cotton)
    return {
      ...p,
      yarn: quote ? quote.price : p.yarn,
      candy: lot ? lotPrice(lot, market).candy : p.candy,
    }
  })
}

/** The single-product inputs with the market's prices laid over the typed
 *  ones wherever a price is following the market. */
export function resolveInputs(
  inputs: ProfitInputs, single: SinglePricing, market: Market | null,
): ProfitInputs {
  const quote = single.yarnAuto ? yarnQuote(market, single) : null
  const grade = single.cottonAuto ? findGrade(market, single.grade) : null
  return {
    ...inputs,
    yarn: quote ? quote.price : inputs.yarn,
    candy: grade ? grade.candy : inputs.candy,
  }
}

export function seedSingle(
  def: { count: number; quality: string } | undefined, market: Market | null,
): SinglePricing {
  if (!def) return MANUAL_SINGLE
  const mc = findCount(market, def.count, def.quality)
  const grade = findGrade(market, mc?.grade ?? null)
  return {
    count: mc ? def.count : null, quality: mc ? def.quality : null,
    grade: grade?.key ?? null,
    yarnAuto: Boolean(mc?.yarn), cottonAuto: Boolean(grade),
  }
}

/** A saved case that predates the market: its prices are the ones typed. */
export const MANUAL_SINGLE: SinglePricing = {
  count: null, quality: null, grade: null, yarnAuto: false, cottonAuto: false,
}

// ------------------------------------------------------------------ dates
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2026-05-01" -> "May 2026" */
export function monthYear(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m] = iso.split('-')
  return `${MONTHS[Number(m) - 1]} ${y}`
}

/** "2026-10-03" -> "3 Oct 2026" */
export function dayMonthYear(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`
}

/** Whole months from the yarn sheet's last month to today. */
export function monthsOld(iso: string | null | undefined, today = new Date()): number {
  if (!iso) return 0
  const [y, m] = iso.split('-').map(Number)
  return (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m)
}
